import { DatePipe, DecimalPipe } from '@angular/common';
import {
  Component,
  OnInit,
  ViewChild,
  AfterViewInit,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import {
  MatCellDef,
  MatHeaderCellDef,
  MatHeaderRowDef,
  MatRowDef,
  MatTableModule,
  MatTableDataSource,
} from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DateTime } from 'luxon';

import type { contractOutput } from '#backend/contract';
import { ConsolidatedPlotCurrentStatusEnum } from '#backend/entity/loaded/harvest-monitoring/ConsolidatedPlotCurrentStatus';
import {
  NDVIDialogComponent,
  NDVIDialogData,
} from '../../../../../shared/components/ndvi-dialog/ndvi-dialog.component';
import { MonitoringResultsStateService } from '../../../../../shared/services/monitoring-results-state.service';
import { SmartOrpcClientService } from '../../../../../shared/services/smart-orpc-client.service';
import {
  getPlotStatusName,
  getStatusColorRgb,
} from '../../../../../shared/utils/monitoredPlotStatus';
import { NDVIDataPoint } from '../../../../../shared/components/ndvi-chart/ndvi-chart.component';
import { TypeSafeMatCellDef } from '../../../../../type-safe-mat-cell-def.directive';
import { PlotsMapComponent } from '../../plots-map/plots-map.component';
import { UserService } from '../../../../../shared/services/user.service';
import { UserRole } from '#backend/common/user-types';

type AreaDetailResponse = contractOutput['monitoring']['areaDetail'];
type PlotData = AreaDetailResponse['plots'][number];
type AreaInfo = AreaDetailResponse['areaInfo'];

export interface MonitoredAreaPlot {
  plotId: number;
  plotStatus: string;
  plotArea: number | null;
  emergencyDate: string | null;
  senescenceDate: string | null;
  harvestDate: string | null;
  finalHarvestDate: string | null;
  zarcRisk: number | null;
  /**
   * ISO datetime string do último cálculo de ZARC.
   * null   → nunca processado → exibir "-" ou vazio
   * string → calculado; verificar zarcRisk:
   *            não-null → mostrar percentual
   *            null     → mostrar "Sem Zoneamento"
   */
  zarcCalculatedAt: string | null;
  lastSentinelDate: string | null;
  metricsProcessingDate: string | null;
  warning: boolean;
  alert: boolean;
}

@Component({
  selector: 'app-monitored-area',
  imports: [
    RouterModule,
    MatIcon,
    MatButtonModule,
    MatDialogModule,
    MatHeaderCellDef,
    MatCellDef,
    MatHeaderRowDef,
    MatRowDef,
    MatTableModule,
    MatSortModule,
    DatePipe,
    DecimalPipe,
    MatButtonModule,
    MatCardModule,
    MatCellDef,
    MatHeaderCellDef,
    MatHeaderRowDef,
    MatIcon,
    MatPaginator,
    MatProgressBarModule,
    MatRowDef,
    MatTableModule,
    PlotsMapComponent,
    RouterModule,
    TypeSafeMatCellDef,
    MatTooltipModule,
  ],
  templateUrl: './monitored-area.component.html',
  styleUrl: './monitored-area.component.scss',
})
export class MonitoredAreaComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  monitoringAreaIds: number[] = [];

  cropYear = '';

  // Signals for reactive data
  readonly isLoading = signal(false);
  readonly areaInfo = signal<AreaInfo | null>(null);
  readonly indicators = signal<AreaDetailResponse['indicators'] | null>(null);
  readonly dataSource = signal<MatTableDataSource<MonitoredAreaPlot>>(
    new MatTableDataSource<MonitoredAreaPlot>([]),
  );
  readonly totalPlots = signal<number>(0);
  readonly idArea = input<string | null>(null);
  readonly selectedPlot = signal<MonitoredAreaPlot | null>(null);

  // Pagination state
  pageSize = 10;
  pageIndex = 0;

  // Sort state
  private currentSort: Sort = { active: 'id', direction: 'asc' };

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly smartClient = inject(SmartOrpcClientService);
  private readonly stateService = inject(MonitoringResultsStateService);
  private readonly dialog = inject(MatDialog);
  private readonly userService = inject(UserService);

  // Computed property to check if user is admin (Geoambiente)
  readonly isAdminUser = computed(() => {
    const role = this.userService.user()?.role;
    return (
      role === UserRole.ADMIN_GEOAMBIENTE ||
      role === UserRole.OPERADOR_GEOAMBIENTE
    );
  });

  legends = computed<
    {
      indicatorKey: keyof AreaDetailResponse['indicators'];
      status: ConsolidatedPlotCurrentStatusEnum;
    }[]
  >(() => {
    return [
      {
        indicatorKey: 'awaitingPlantingAreaHa',
        status: 'awaiting_planting',
      },
      {
        indicatorKey: 'awaitingIdentificationAreaHa',
        status: 'awaiting_identification',
      },
      {
        indicatorKey: 'possiblyTargetCultureAreaHa',
        status: 'possibly_target_culture',
      },
      {
        indicatorKey: 'developingAreaHa',
        status: 'developing',
      },
      {
        indicatorKey: 'senescenceAreaHa',
        status: 'senescence',
      },
      {
        indicatorKey: 'readyForHarvestAreaHa',
        status: 'ready_for_harvest',
      },
      {
        indicatorKey: 'harvestedAreaHa',
        status: 'harvested',
      },
      {
        indicatorKey: 'divergentCultureAreaHa',
        status: 'divergent_culture',
      },
      {
        indicatorKey: 'notPlantedAreaHa',
        status: 'not_planted',
      },
    ];
  });

  async ngOnInit(): Promise<void> {
    // Recupera lista de IDs via query param (monitoringAreaIds=1,2,3)
    const queryIds = this.route.snapshot.queryParamMap.get('monitoringAreaIds');
    if (queryIds) {
      this.monitoringAreaIds = queryIds
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v.length > 0)
        .map((v) => Number(v))
        .filter((n) => !isNaN(n));
    }

    if (this.monitoringAreaIds.length > 0) {
      await this.loadAreaDetail();
    }

    // Update displayed columns based on user role
    this.updateDisplayedColumns();
  }

  ngAfterViewInit() {
    // Configurar ordenação
    if (this.sort) {
      const ds = this.dataSource();
      ds.sort = this.sort;
      // Define ordenação inicial
      this.sort.active = 'id';
      this.sort.direction = 'asc';
    }
  }

  onSortChange(event: Sort) {
    this.currentSort = event;
    this.pageIndex = 0; // Reset para primeira página ao ordenar

    void this.loadAreaDetail();
  }

  getPlotStatus(status: ConsolidatedPlotCurrentStatusEnum): string {
    return (
      getPlotStatusName(status, this.areaInfo()?.cultureName) ??
      'Estado desconhecido'
    );
  }

  /**
   * Updates the displayed columns based on user permissions
   * Hides plotId column for client users (non-admin)
   */
  private updateDisplayedColumns(): void {
    const baseColumns = [
      'rowNumber',
      'warningAlert',
      'plotStatus',
      'plotArea',
      'emergencyDate',
      'senescenceDate',
      'harvestDate',
      'finalHarvestDate',
      'zarc',
      'timeSeries',
    ];

    // If admin, show plotId column before timeSeries
    if (this.isAdminUser()) {
      const timeSeriesIndex = baseColumns.indexOf('timeSeries');
      this.displayedColumns = [
        ...baseColumns.slice(0, timeSeriesIndex),
        'plotId',
        ...baseColumns.slice(timeSeriesIndex),
      ];
    } else {
      this.displayedColumns = baseColumns;
    }
  }

  displayedColumns: string[] = [];

  getLegendCssColor(status: ConsolidatedPlotCurrentStatusEnum) {
    const [r, g, b] = getStatusColorRgb(status);
    return `rgb(${r} ${g} ${b})` as const;
  }

  /**
   * Handle pagination events
   */
  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.loadAreaDetail();
  }

  private async loadAreaDetail(): Promise<void> {
    if (this.monitoringAreaIds.length === 0) {
      return;
    }

    this.isLoading.set(true);
    try {
      // Converte ordenação do Material para formato do backend
      // Mapeamento de colunas do frontend para backend
      const columnMapping: Record<string, string> = {
        plotId: 'id',
        plotArea: 'areaHa',
        plotStatus: 'plotStatus', // campo especial: ordenado via JOIN currentStatus.plotStatus no repositório
        emergencyDate: 'emergencyDate',
        senescenceDate: 'senescenceDate',
        harvestDate: 'harvestDate',
        finalHarvestDate: 'srcFinalHarvestDate',
        zarc: 'zarcRisk',
      };

      const backendColumn =
        columnMapping[this.currentSort.active] || this.currentSort.active;
      const orderBy: Record<string, 'ASC' | 'DESC'> =
        this.currentSort.active && this.currentSort.direction
          ? {
              [backendColumn]: this.currentSort.direction.toUpperCase() as
                'ASC' | 'DESC',
            }
          : { id: 'ASC' };

      const { data, error } =
        await this.smartClient.safeClient.monitoring.areaDetail({
          monitoringAreaIds: this.monitoringAreaIds,
          cursor: this.pageIndex * this.pageSize,
          limit: this.pageSize,
          orderBy,
        });

      if (error) {
        return;
      }

      if (!data) {
        return;
      }

      // Update area info (only on first load)
      if (this.pageIndex === 0 || !this.areaInfo()) {
        this.areaInfo.set(data.areaInfo);
      }

      // Update indicators (only on first load)
      if (this.pageIndex === 0 || !this.indicators()) {
        this.indicators.set(data.indicators);
      }

      // Update total plots count
      this.totalPlots.set(data.totalPlots);

      // Map plots to table format
      const plots = data.plots.map((plot) => this.mapPlotToTableRow(plot));
      this.dataSource().data = plots;
    } finally {
      this.isLoading.set(false);
    }
  }

  private mapPlotToTableRow(plot: PlotData): MonitoredAreaPlot {
    // Issue #636: É preciso subtrair de `emergencyDate` os dias em `plot.subtractionDays` antes de exibir na tabela
    const emergencyDate =
      plot.subtractionDays == null || plot.subtractionDays === 0
        ? plot.emergencyDate
        : plot.emergencyDate && plot.subtractionDays
          ? DateTime.fromISO(plot.emergencyDate)
              .minus({ days: plot.subtractionDays })
              .toISODate()
          : null;

    return {
      plotId: plot.id,
      plotStatus: this.getPlotStatusDescription(
        plot.currentStatus ?? undefined,
        this.areaInfo()?.cultureName ?? undefined,
      ),
      plotArea: plot.areaHa,
      emergencyDate,
      senescenceDate: plot.senescenceDate,
      harvestDate: plot.harvestDate,
      finalHarvestDate: plot.finalHarvestDate,
      zarcRisk: plot.zarcRisk,
      zarcCalculatedAt: plot.zarcCalculatedAt,
      lastSentinelDate: plot.lastSentinelDate,
      metricsProcessingDate: plot.metricsProcessingDate,
      warning: plot.warning ?? false,
      alert: plot.alert ?? false,
    };
  }

  /**
   * Número sequencial da linha considerando paginação (1-based)
   */
  getRowNumber(index: number): number {
    return this.pageIndex * this.pageSize + index + 1;
  }

  private getPlotStatusDescription(
    status?: ConsolidatedPlotCurrentStatusEnum,
    cultureName?: string,
  ): string {
    const statusName = getPlotStatusName(status, cultureName);
    return statusName ?? 'Desconhecido';
  }

  /**
   * Volta para a tela de resultados de monitoramento,
   * preservando os filtros e destacando a linha selecionada
   */
  goBack(): void {
    const areaId = this.idArea();
    const requestId = this.areaInfo()?.requestId;
    // Armazena o ID da área selecionada para destacar a linha
    if (areaId && requestId) {
      this.stateService.setSelectedRowId({
        areaId,
        requestId,
      });
    }

    // Obtém os queryParams atuais (que contêm os filtros)
    const queryParams = this.route.snapshot.queryParams;

    // Navega de volta para a tela de resultados preservando os queryParams
    this.router.navigate(['/monitoring-safras', 'results'], {
      queryParams,
      queryParamsHandling: 'preserve',
    });
  }

  /**
   * Abre o dialog do gráfico NDVI para o talhão selecionado
   */
  async openNDVIChart(plot: MonitoredAreaPlot): Promise<void> {
    try {
      // Busca dados NDVI reais da API
      const { data: ndviData, error } =
        await this.smartClient.safeClient.monitoring.plotNDVI({
          plotId: plot.plotId,
          monthsAgo: 24,
        });

      if (error) {
        return;
      }

      if (!ndviData || ndviData.length === 0) {
        console.warn(
          `Nenhum dado NDVI encontrado para o talhão ${plot.plotId}. Usando dados simulados.`,
        );

        this.openNDVIDialog(plot, []);
        return;
      }

      // Transformar dados do backend (Date objects) para o formato esperado pelo componente (strings)
      const transformedData = ndviData.map((item) => ({
        date: item.date.toISOString().split('T')[0], // Converte Date para YYYY-MM-DD
        ndvi: item.ndvi,
        monitoringId: item.monitoringId || '',
        rawNdvi: item.rawNdvi || null,
        interpolatedNdvi: item.interpolatedNdvi || null,
        smoothedNdvi: item.smoothedNdvi || null,
      }));

      this.openNDVIDialog(plot, transformedData);
    } catch (error) {
      console.error('Erro ao buscar dados NDVI:', error);
      // Fallback para dados simulados em caso de erro

      this.openNDVIDialog(plot, []);
    }
  }

  /**
   * Abre o dialog NDVI com os dados fornecidos
   */
  private openNDVIDialog(
    plot: MonitoredAreaPlot,
    ndviData: NDVIDataPoint[],
  ): void {
    const dialogData: NDVIDialogData = {
      data: ndviData,
      id: plot.plotId,
      cultura: this.areaInfo()?.cultureName || null,
      areaTalhaoHa: plot.plotArea || 0,
      cropYear: this.areaInfo()?.cropYear || null,
      statusCulture: plot.plotStatus,
      title: `Série Temporal NDVI`,
    };
    console.log(dialogData);
    this.dialog.open(NDVIDialogComponent, {
      data: dialogData,
      width: '95vw',
      maxWidth: '1400px',
      height: '85vh',
      maxHeight: '900px',
      panelClass: ['ndvi-dialog-container'],
      hasBackdrop: true,
      disableClose: false,
    });
  }

  handleTableRowClick(row: MonitoredAreaPlot) {
    if (row.plotId === this.selectedPlot()?.plotId) {
      this.selectedPlot.set(null);
    } else {
      this.selectedPlot.set(row);
    }
  }

  onPlotClicked($event: number) {
    const plotInPage = this.dataSource().data.find((p) => p.plotId === $event);
    if (plotInPage?.plotId === this.selectedPlot()?.plotId) {
      this.selectedPlot.set(null);
    } else if (plotInPage) {
      this.selectedPlot.set(plotInPage);
    }
  }
}
